import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { CalendarClock, Flame, History as HistoryIcon } from "lucide-react";
import { historyPeriodsQuery } from "@/lib/history-queries";
import { monthName, monthSlug, type HistoryPeriod } from "@/lib/history";
import { BOARDS_OG_IMAGE, BOARDS_OG_IMAGE_ALT, SITE_URL, buildPageMetadata } from "@/lib/seo";
import { translate, useT } from "@/i18n";
import { detectLocale } from "@/i18n/runtime";
import { formatHeat } from "@/lib/behot";

export const Route = createFileRoute("/history/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(historyPeriodsQuery),
  head: () => {
    const locale = detectLocale();
    return buildPageMetadata({
      path: "/history",
      title: translate(locale, "history.index.title"),
      description: translate(locale, "history.index.description"),
      ogTitle: translate(locale, "history.index.h1"),
      ogDescription: translate(locale, "history.index.description"),
      image: BOARDS_OG_IMAGE,
      imageAlt: BOARDS_OG_IMAGE_ALT,
      keywords: [
        "BeHot.Now rankings archive",
        "monthly rankings history",
        "past leaderboard rankings",
      ],
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: translate(locale, "history.index.h1"), path: "/history" },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: translate(locale, "history.index.h1"),
        url: `${SITE_URL}/history`,
        description: translate(locale, "history.index.description"),
        isPartOf: { "@type": "WebSite", url: SITE_URL, name: "BeHot.Now" },
      },
    });
  },
  component: HistoryIndexPage,
});

function groupByYear(periods: HistoryPeriod[]) {
  const map = new Map<number, HistoryPeriod[]>();
  for (const p of periods) {
    const list = map.get(p.year) ?? [];
    list.push(p);
    map.set(p.year, list);
  }
  return [...map.entries()].sort((a, b) => b[0] - a[0]);
}

function HistoryIndexPage() {
  const { t, locale } = useT();
  const { data: periods } = useSuspenseQuery(historyPeriodsQuery);
  const years = groupByYear(periods);

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          BeHot.Now
        </Link>
        <span className="px-1.5">/</span>
        <span className="text-foreground">{t("history.nav")}</span>
      </nav>

      <header className="mt-4">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
          <HistoryIcon className="size-3.5" />
          {t("history.pastRankings")}
        </span>
        <h1 className="mt-3 font-display text-3xl font-black sm:text-4xl">
          {t("history.index.h1")}
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">{t("history.index.intro")}</p>
      </header>

      {years.length === 0 ? (
        <p className="mt-10 rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          {t("history.index.empty")}
        </p>
      ) : (
        <div className="mt-10 space-y-10">
          {years.map(([year, months]) => (
            <section key={year}>
              <h2 className="font-display text-2xl font-bold">
                <Link
                  to="/history/$year"
                  params={{ year: String(year) }}
                  className="hover:text-primary"
                >
                  {year}
                </Link>
              </h2>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {months.map((m) => (
                  <li key={m.id}>
                    <Link
                      to="/history/$year/$month"
                      params={{ year: String(m.year), month: monthSlug(m.month) }}
                      className="card-surface flex items-center gap-3 p-4 transition-colors hover:border-primary/50"
                    >
                      <CalendarClock className="size-5 text-primary" />
                      <span className="min-w-0 flex-1">
                        <span className="block font-display font-semibold capitalize">
                          {monthName(m.month, locale)} {m.year}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {t("history.entries", { n: m.entry_count })}
                          {" · "}
                          <Flame className="inline size-3 text-primary" />{" "}
                          {formatHeat(m.total_heat_cents)}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
